import type { VoidFunctionComponent } from 'react';

import { plural } from '../../../../shared/helpers';
import Icon, { icons } from '../../generic/Icon';
import { RouterLink } from '../../generic/Link';
import LicenseDetail from '../../licenses/LicenseDetail';
import type { License, Project, Revision } from '../../../types';

import PreviewContentFailing from './PreviewContentFailing';
import PreviewContentPassing from './PreviewContentPassing';

import styles from './ProjectPreview.module.less';

type Props = {
  baseURL: string;
  deepestLevel: number;
  issueCount: number;
  projectName: string;
  selectedLicense: License | null;
  licenseCount: number;
  dependencyCount: number;
  isLimitedScan: boolean;
  unresolvedIssueCounts: Revision['unresolved_issue_counts'];
  project: Project;
};

/**
 * Main preview section showing scan details and license information
 */
const PreviewSection: VoidFunctionComponent<Props> = ({
  baseURL,
  deepestLevel,
  issueCount,
  projectName,
  selectedLicense,
  licenseCount,
  dependencyCount,
  isLimitedScan,
  unresolvedIssueCounts,
  project,
}) => (
  <div className={`panel panel-flat panel-responsive padded animated fadeInUp ${styles.previewPanel}`}>
    <div className="row">
      {issueCount > 0 ? (
        <PreviewContentFailing unresolvedIssueCounts={unresolvedIssueCounts} project={project} />
      ) : (
        <PreviewContentPassing
          dependencyCount={dependencyCount}
          deepestLevel={deepestLevel}
          isLimitedScan={isLimitedScan}
        />
      )}
      <div className="col-md-5">
        <h3 className="margin-top margin-top-responsive">
          Using <b>{projectName}</b> as a dependency
        </h3>
        <p>
          {selectedLicense ? (
            <div>
              This project is licensed under the terms of the <b>{selectedLicense.name}</b> and
              contains{' '}
              <RouterLink to={`${baseURL}/browse/licenses`}>
                {licenseCount - 1} other licenses
              </RouterLink>
              .
            </div>
          ) : (
            <div>
              This project contains{' '}
              <RouterLink to={`${baseURL}/browse/licenses`}>{licenseCount} {plural(licenseCount, 'license')}</RouterLink>.
            </div>
          )}
        </p>
        {licenseCount > 0 && (
          <RouterLink className="btn btn-primary" to={`${baseURL}/browse/licenses`}>
            View Details &raquo;
          </RouterLink>
        )}
        <br />
        <br />
        {selectedLicense && (
          <div>
            <h4>About {selectedLicense.name}:</h4>
            <LicenseDetail license={selectedLicense} />
          </div>
        )}
      </div>
    </div>

    <div className="text-center">
      <br className="hidden-xs" />
      <span className={styles.scrollHintText}>Scroll down for more details</span>
      <br />
      <span className="animated fadeInDown">
        <Icon icon={icons.chevronDown} />
      </span>
    </div>
  </div>
);

export default PreviewSection;
